// src/components/Warehouses/WarehouseProducts/ProductTransactions/index.js

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../../../UserContext";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaArrowLeft,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaSeedling,
  FaWarehouse,
  FaClock,
  FaBox,
} from "react-icons/fa";
import "./style.css";

// --- Simplified Transaction Card Component ---
const TransactionCard = ({
  movement,
  productUnit,
  runningBalance,
  fieldsMap,
  locationsMap,
  currentWarehouseId,
}) => {
  const { t, i18n } = useTranslation();
  const isStockIn = movement.type === "in";

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t("Today");
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t("Yesterday");
    }

    return date.toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(i18n.language === "ar" ? "ar-EG" : "en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get simplified transaction type
  const getTransactionType = (movement) => {
    const movementType = movement.movementType?.toLowerCase();

    if (movementType) {
      switch (movementType) {
        case "load":
          return t("Stock Added");
        case "usage":
          return movement.fieldId ? t("Field Usage") : t("Used");
        case "allocate":
        case "transfer":
          return t("Transfer");
        case "other":
          return t("Adjustment");
        default:
          return isStockIn ? t("Added") : t("Used");
      }
    }

    // For stock-in movements, differentiate between harvest, purchase, and manual load
    if (isStockIn) {
      // Check if this is from a purchase invoice
      if (movement.invoiceId && movement.supplierId) {
        return t("Purchase");
      }
      // Check if this is from a purchase invoice (invoiceId alone is enough)
      if (movement.invoiceId) {
        return t("Purchase");
      }
      // Check if this is a harvest (no invoice, no supplier)
      if (!movement.invoiceId && !movement.supplierId) {
        return t("Harvest");
      }
      // Fallback for stock in
      return t("Stock In");
    }

    // Fallback logic for stock out
    if (movement.fieldId) return t("Field Usage");
    if (movement.transferPaired) return t("Transfer");
    return t("Stock Out");
  };

  // Get location/field context
  const getContext = (movement) => {
    // For field usage, show the field name
    if (movement.fieldId) {
      if (fieldsMap && fieldsMap[movement.fieldId]) {
        return fieldsMap[movement.fieldId];
      }
      // Fallback to showing field ID if name not found
      return `${t("Field")} #${movement.fieldId}`;
    }

    // For transfers, show the destination location (for outgoing) or source (for incoming)
    if (movement.transferPaired) {
      // For outgoing transfers, show where it's going to
      if (
        movement.type === "out" &&
        movement.destinationLocationId &&
        locationsMap[movement.destinationLocationId]
      ) {
        return `${t("To")}: ${locationsMap[movement.destinationLocationId]}`;
      }
      // For incoming transfers, show where it came from
      if (
        movement.type === "in" &&
        movement.sourceLocationId &&
        locationsMap[movement.sourceLocationId]
      ) {
        return `${t("From")}: ${locationsMap[movement.sourceLocationId]}`;
      }
    }

    // For other movements with location, only show if it's not the current warehouse
    if (
      movement.locationId &&
      movement.locationId !== currentWarehouseId &&
      locationsMap &&
      locationsMap[movement.locationId]
    ) {
      return locationsMap[movement.locationId];
    }

    // Don't show any location context if it's the current warehouse
    return null;
  };

  return (
    <div
      className={`transaction-card-simple ${
        isStockIn ? "stock-in" : "stock-out"
      }`}
    >
      {/* Movement Direction Indicator */}
      <div className={`direction-indicator ${isStockIn ? "in" : "out"}`}>
        {isStockIn ? <FaArrowUp /> : <FaArrowDown />}
      </div>

      {/* Main Content Area */}
      <div className="transaction-content">
        {/* Header Row */}
        <div className="transaction-row header-row">
          <div className="left-section">
            <span className="transaction-type">
              {getTransactionType(movement)}
            </span>
            {getContext(movement) && (
              <span className="transaction-context">
                • {getContext(movement)}
              </span>
            )}
          </div>
          <div className="right-section">
            <span className="transaction-date">
              {formatDate(movement.date)}
            </span>
            <span className="transaction-time">
              {formatTime(movement.date)}
            </span>
          </div>
        </div>

        {/* Details Row */}
        <div className="transaction-row details-row">
          <div className="left-section">
            <span className={`quantity ${isStockIn ? "positive" : "negative"}`}>
              {isStockIn ? "+" : "-"}
              {movement.quantity} {productUnit}
            </span>
            {movement.price && (
              <span className="price-info">@ ${movement.price}/unit</span>
            )}
          </div>
          <div className="right-section">
            {runningBalance !== undefined && (
              <div className="balance-info">
                <span className="balance-label">{t("Balance")}:</span>
                <span className="balance-value">
                  {runningBalance} {productUnit}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Notes if present */}
        {movement.movementNotes && (
          <div className="transaction-row notes-row">
            <span className="notes-text">{movement.movementNotes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Product Transactions Component ---
const ProductTransactions = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { token } = useContext(UserContext);

  const [product, setProduct] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [movements, setMovements] = useState([]);
  const [calculatedBalance, setCalculatedBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fieldsMap, setFieldsMap] = useState({});
  const [locationsMap, setLocationsMap] = useState({});

  const fetchData = useCallback(async () => {
      if (!productId || !token) return;
      setLoading(true);
      try {
        // Step 1: Fetch product details and all its movements
        const productRes = await axios.get(
          `api/inventoryItemDetails/${productId}`,
          {
            headers: { Authorization: token },
          }
        );
        const fetchedProduct = productRes.data.items[0];
        
        // Check if we have a warehouse ID passed via location state (for transferred products)
        const currentWarehouseId = location.state?.warehouseId || fetchedProduct.locationId;
        
        // Update product with correct location for display
        const productWithCorrectLocation = {
          ...fetchedProduct,
          locationId: currentWarehouseId
        };
        setProduct(productWithCorrectLocation);

        const movementsRes = await axios.get(
          `api/inventoryMovements/findByInventoryItemDetailId/${productId}`,
          { headers: { Authorization: token } }
        );

        // Step 2: Fetch warehouse details using the current warehouse ID
        if (currentWarehouseId) {
          const warehouseRes = await axios.get(
            `api/inventoryLocation/${currentWarehouseId}`,
            {
              headers: { Authorization: token },
            }
          );
          setWarehouse(warehouseRes.data.items[0]);
        }

        // Step 3: Process movements and calculate current balance
        const rawMovements = movementsRes.data.items[0]?.InventoryMovements || {
          Procurements: [],
          Distributions: [],
        };

        const procurements = (rawMovements.Procurements || []).map((p) => ({
          ...p,
          type: "in",
          date: p.procurementDate,
          movementType: p.movementType,
        }));
        const distributions = (rawMovements.Distributions || []).map((d) => ({
          ...d,
          type: "out",
          date: d.distributionDate,
          movementType: d.movementType,
        }));

        // Step 4: Fetch unique field and location IDs to get their names
        const fieldIds = new Set();
        const locationIds = new Set();

        [...procurements, ...distributions].forEach((movement) => {
          // Check for field ID in different possible properties
          if (movement.fieldId) {
            fieldIds.add(movement.fieldId);
          } else if (movement.field_id) {
            fieldIds.add(movement.field_id);
            movement.fieldId = movement.field_id; // Normalize the property name
          }

          if (movement.locationId) locationIds.add(movement.locationId);
        });

        // Fetch field names if there are any field IDs
        const fieldNamesMap = {};
        if (fieldIds.size > 0) {
          try {
            const fieldPromises = Array.from(fieldIds).map(async (fieldId) => {
              try {
                const fieldRes = await axios.get(`api/field/${fieldId}`, {
                  headers: { Authorization: token },
                });
                const field = fieldRes.data.items?.[0];
                if (field) {
                  // Field has a single 'name' property, not separate Ar/En
                  fieldNamesMap[fieldId] = field.name || `Field ${fieldId}`;
                }
              } catch (err) {
                console.error(`Failed to fetch field ${fieldId}:`, err);
                fieldNamesMap[fieldId] = `Field ${fieldId}`;
              }
            });
            await Promise.all(fieldPromises);
          } catch (err) {
            console.error("Error fetching field names:", err);
          }
        }
        setFieldsMap(fieldNamesMap);

        // Fetch location names if there are any location IDs
        const locationNamesMap = {};
        if (locationIds.size > 0) {
          try {
            const locationPromises = Array.from(locationIds).map(
              async (locationId) => {
                try {
                  const locationRes = await axios.get(
                    `api/inventoryLocation/${locationId}`,
                    {
                      headers: { Authorization: token },
                    }
                  );
                  const location = locationRes.data.items?.[0];
                  if (location) {
                    locationNamesMap[locationId] =
                      i18n.language === "ar"
                        ? location.nameAr ||
                          location.nameEn ||
                          `Location ${locationId}`
                        : location.nameEn ||
                          location.nameAr ||
                          `Location ${locationId}`;
                  }
                } catch (err) {
                  console.error(`Failed to fetch location ${locationId}:`, err);
                  locationNamesMap[locationId] = `Location ${locationId}`;
                }
              }
            );
            await Promise.all(locationPromises);
          } catch (err) {
            console.error("Error fetching location names:", err);
          }
        }
        setLocationsMap(locationNamesMap);

        // Combine and detect paired movements (transfers)
        const allMovements = [...procurements, ...distributions];
        const currentLocationId = currentWarehouseId;

        // Mark paired transfers and filter to show only relevant movements for current warehouse
        const processedMovements = [];
        const pairedIndices = new Set();

        allMovements.forEach((move, index) => {
          // Skip if already paired
          if (pairedIndices.has(index)) return;

          const timestamp = new Date(move.date).getTime();
          const pairedIndex = allMovements.findIndex(
            (m, i) =>
              i !== index &&
              !pairedIndices.has(i) &&
              Math.abs(new Date(m.date).getTime() - timestamp) < 1000 && // Within 1 second
              m.type !== move.type &&
              m.quantity === move.quantity
          );

          if (pairedIndex !== -1) {
            const paired = allMovements[pairedIndex];
            // This is a transfer - mark both as paired
            pairedIndices.add(index);
            pairedIndices.add(pairedIndex);

            // Only show the movement relevant to current warehouse
            if (move.locationId === currentLocationId) {
              // This movement is for current warehouse
              move.transferPaired = true;
              if (move.type === "in") {
                move.sourceLocationId = paired.locationId;
              } else {
                move.destinationLocationId = paired.locationId;
              }
              processedMovements.push(move);
            } else if (paired.locationId === currentLocationId) {
              // The paired movement is for current warehouse
              paired.transferPaired = true;
              if (paired.type === "in") {
                paired.sourceLocationId = move.locationId;
              } else {
                paired.destinationLocationId = move.locationId;
              }
              processedMovements.push(paired);
            }
          } else {
            // Not a transfer, just a regular movement
            if (move.locationId === currentLocationId) {
              processedMovements.push(move);
            }
          }
        });
        // Sort chronologically (oldest first) to calculate running balance
        processedMovements.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Calculate running balance for each movement
        let runningTotal = 0;
        const movementsWithBalance = processedMovements.map((move) => {
          if (move.type === "in") {
            runningTotal += move.quantity;
          } else {
            runningTotal -= move.quantity;
          }
          return { ...move, runningBalance: runningTotal };
        });

        // Reverse to show newest first
        movementsWithBalance.reverse();
        setMovements(movementsWithBalance);

        // --- Balance Calculation ---
        // Calculate balance based on processed movements for current warehouse only
        const procurementsTotal = processedMovements
          .filter(m => m.type === "in")
          .reduce((sum, m) => sum + m.quantity, 0);
        
        const distributionsTotal = processedMovements
          .filter(m => m.type === "out")
          .reduce((sum, m) => sum + m.quantity, 0);
        
        const balance = procurementsTotal - distributionsTotal;
        setCalculatedBalance(balance);
      } catch (error) {
        console.error("Failed to fetch product data:", error);
        toast.error(t("Failed to load product transactions."));
      } finally {
        setLoading(false);
      }
  }, [productId, token, t, i18n.language, location.state?.warehouseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh on navigation return
  useEffect(() => {
    if (location.state?.refresh) {
      fetchData();
      // Clear refresh flag but preserve warehouseId
      navigate(location.pathname, {
        replace: true,
        state: { warehouseId: location.state?.warehouseId }
      });
    }
  }, [location.state?.refresh, location.state?.warehouseId, fetchData, navigate, location.pathname]);

  const getDisplayName = (item, keyAr, keyEn) => {
    if (!item) return "";
    return i18n.language === "ar" ? item[keyAr] : item[keyEn] || item[keyAr];
  };

  if (loading) {
    return <div>{t("Loading...")}</div>;
  }

  const productName = getDisplayName(product, "nameAr", "nameEn");
  const warehouseName = getDisplayName(warehouse, "nameAr", "nameEn");

  // Calculate summary statistics (already filtered for current warehouse)
  const totalIn = movements
    .filter((m) => m.type === "in")
    .reduce((sum, m) => sum + m.quantity, 0);

  const totalOut = movements
    .filter((m) => m.type === "out")
    .reduce((sum, m) => sum + m.quantity, 0);

  return (
    <div className="product-transactions-container">
      <header className="view-header">
        <div className="header-left-content">
          <button
            className="button-icon-only"
            onClick={() => navigate(-1)}
            aria-label={t("Go back")}
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="view-header-title">{productName}</h1>
            <p className="view-header-subtitle">
              {warehouseName} • {movements.length} {t("transactions")}
            </p>
          </div>
        </div>
        <button
          className="button-primary"
          onClick={() =>
            navigate(`/inventory/product/${productId}/transaction/new`, {
              state: { warehouseId: warehouse?.id || product?.locationId }
            })
          }
        >
          <FaPlus /> {t("Add Movement")}
        </button>
      </header>

      {/* Summary Stats Section */}
      <div className="product-transaction-summary">
        <div className="product-transaction-summary-card current-balance">
          <div className="product-transaction-summary-label">{t("Current Stock")}</div>
          <div className="product-transaction-summary-value">
            {calculatedBalance} <span className="unit">{product?.unit}</span>
          </div>
        </div>
        <div className="product-transaction-summary-card total-in">
          <div className="product-transaction-summary-label">{t("Total In")}</div>
          <div className="product-transaction-summary-value">
            +{totalIn} <span className="unit">{product?.unit}</span>
          </div>
        </div>
        <div className="product-transaction-summary-card total-out">
          <div className="product-transaction-summary-label">{t("Total Out")}</div>
          <div className="product-transaction-summary-value">
            -{totalOut} <span className="unit">{product?.unit}</span>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="transactions-list">
        {movements.length > 0 ? (
          <>
            <h2 className="section-title">{t("Transaction History")}</h2>
            {movements.map((move) => (
              <TransactionCard
                key={`${move.type}-${move.id}`}
                movement={move}
                productUnit={product?.unit}
                runningBalance={move.runningBalance}
                fieldsMap={fieldsMap}
                locationsMap={locationsMap}
                currentWarehouseId={warehouse?.id || product?.locationId}
              />
            ))}
          </>
        ) : (
          <div className="no-data-message">
            <FaBox className="empty-icon" />
            <p>{t("No transactions found for this product.")}</p>
            <p className="help-text">
              {t("Add your first transaction to get started.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductTransactions;
